/**
 * Clinical Summary Types
 *
 * Structured clinical documentation extracted from appointment transcripts
 * Stored directly in Transcript collection for single source of truth
 */

/**
 * Highlight Entity - Represents a highlighted span of text in clinical content
 */
export interface HighlightEntity {
  text: string;           // The highlighted text span
  type: string;           // 'symptom', 'allergy', 'smoking', 'hallucination', etc.
  category: string;       // 'clinical', 'safety', 'behavioral', 'system_relationship'
  startIndex: number;     // Character position in content
  endIndex: number;
  confidence: number;     // 0-1
  severity?: string;      // 'low', 'medium', 'high', 'critical' - for safety-critical items
}

/**
 * Content Highlights - AI-powered highlighting metadata
 */
export interface ContentHighlights {
  highlights: HighlightEntity[];
  analyzedAt: string | Date;
  model: string;          // 'gpt-4-turbo'
  version: string;        // '1.0'
}

export interface ClinicalSection {
  // AI-generated content
  content: string;
  status: 'captured' | 'not_captured';
  confidence: number;
  evidenceSegments?: string[];  // Segment IDs that support this section

  // Physician enrichment (dictated or typed)
  physicianNotes?: {
    richText: string;  // HTML from rich text editor
    plainText: string;  // Plain text for search/export
    updatedAt: string | Date;
    updatedBy: string;  // User ID
  };

  // Dictation history (audit trail)
  dictations?: Array<{
    transcription: string;
    audioMetadata: {
      duration: number;  // seconds
      recordedAt: string | Date;
    };
    addedBy: string;  // User ID
  }>;

  // AI-powered content highlighting (clinical safety & documentation)
  contentHighlights?: ContentHighlights;
}

export interface ClinicalSummary {
  chiefComplaint: ClinicalSection;
  historyOfPresentIllness: ClinicalSection;
  pastMedicalHistory: ClinicalSection;
  medications: ClinicalSection;
  familyHistory: ClinicalSection;
  socialHistory: ClinicalSection;
  reviewOfSystems: ClinicalSection;
  physicalExam: ClinicalSection;
  psychiatricHistory: ClinicalSection;
  treatment: ClinicalSection;
  assessment: ClinicalSection;
  plan: ClinicalSection;
}

/**
 * Complete Clinical Summary Data from Transcript
 */
export interface ClinicalSummaryData {
  aiGenerated: ClinicalSummary;
  generatedAt?: string | Date;
  model?: string;
  version?: string;
}

/**
 * Display labels for clinical sections
 */
export const CLINICAL_SECTION_LABELS: Record<keyof ClinicalSummary, string> = {
  chiefComplaint: 'Chief Complaint',
  historyOfPresentIllness: 'History of Present Illness (HPI)',
  pastMedicalHistory: 'Past Medical History (PMH)',
  familyHistory: 'Family History',
  socialHistory: 'Social History',
  psychiatricHistory: 'Psychiatric History',
  medications: 'Medications and Allergies',
  reviewOfSystems: 'Review of Systems',
  physicalExam: 'Physical Exam',
  treatment: 'Treatment',
  assessment: 'Assessment',
  plan: 'Plan'
};

/**
 * Descriptions for clinical sections
 */
export const CLINICAL_SECTION_DESCRIPTIONS: Record<keyof ClinicalSummary, string> = {
  chiefComplaint: 'The main reason for the visit in the patient\'s own words',
  historyOfPresentIllness: 'Detailed symptom description including onset, duration, location, severity, and aggravating/relieving factors',
  pastMedicalHistory: 'Previous procedures, surgeries, injuries, and diseases',
  familyHistory: 'Relevant family conditions that may impact the patient\'s health',
  socialHistory: 'Lifestyle factors like smoking, alcohol use, and drug use',
  psychiatricHistory: 'Mental health history, previous treatments, and current mental state',
  medications: 'Current and past medications, supplements, herbal remedies, and documented allergies',
  reviewOfSystems: 'Review of systems (patient-reported symptoms by body system) and diagnostic/imaging results',
  physicalExam: 'Objective findings the clinician documents observing, testing, or measuring during the exam: vitals, system-based exam findings, and general appearance',
  treatment: 'Medical procedures and interventions performed during this visit, including administered medications, wound care, and refused/unperformed treatments',
  assessment: 'Physician-stated impressions, diagnoses, or differential reasoning for this visit',
  plan: 'Post-visit care plan including imaging, lab work, referrals, prescriptions, follow-up appointments, and wellness recommendations'
};

/**
 * Section order for consistent rendering
 */
export const CLINICAL_SECTION_ORDER: (keyof ClinicalSummary)[] = [
  'chiefComplaint',
  'historyOfPresentIllness',
  'pastMedicalHistory',
  'familyHistory',
  'socialHistory',
  'psychiatricHistory',
  'medications',
  'reviewOfSystems',
  'physicalExam',
  'treatment',
  'assessment',
  'plan'
];
